import React, { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import { useUser } from '../../Hooks/useUser';
import axios from "../../api/axios";

const Skills = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user: contextUser } = useUser();
  const user = props.User ?? contextUser;

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);
  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setSkillToDelete(null);
  };

  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const [category, setCategory] = useState('');
  const [filteredSkills, setFilteredSkills] = useState([]);
  const [bomSkill, setBomSkill] = useState({
    BPSkillsID: 0,
    BP_UserGUID: user?.UserGUID,
    BP_S_Skill: '',
    BP_S_Years: '',
    BP_S_License: '',
    BP_S_Status: '',
  });

  const [userSkills, setUserSkills] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorFields, setErrorFields] = useState({});
  const categories = [{ name: 'Agriculture', value: 'Agriculture' }, { name: 'Aviation', value: 'Aviation' }, { name: 'Business', value: 'Business' }, { name: 'Arts/Drama', value: 'Arts/Drama' }, { name: 'Church Worker', value: 'Church Worker' }, { name: 'Communications', value: 'Communications' }, { name: 'Computers', value: 'Computers' }, { name: 'Construction', value: 'Construction' }, { name: 'Education', value: 'Education' }, { name: 'Emergency Services', value: 'Emergency Services' }, { name: 'Food Service', value: 'Food Service' }, { name: 'Medical/Healthcare', value: 'Medical/Healthcare' }, { name: 'Music', value: 'Music' }, { name: 'Other', value: 'Other' }, { name: 'Sports', value: 'Sports' }];

  // Keep BP_UserGUID synced in case user resolves after mount (admin mode)
  useEffect(() => {
    setBomSkill(prev => ({ ...prev, BP_UserGUID: user?.UserGUID }));
  }, [user?.UserGUID]);

  const resetBomSkill = useCallback(() => {
    setBomSkill({
      BPSkillsID: 0,
      BP_UserGUID: user?.UserGUID,
      BP_S_Skill: '',
      BP_S_Years: '',
      BP_S_License: '',
      BP_S_Status: '',
    });
    setCategory('');
    setFilteredSkills([]);
    setEditMode(false);
  }, [user?.UserGUID]);

  const handleAdd = () => {
    setEditMode(false);
    setCategory('');
    setFilteredSkills([]);
    setErrorFields({});
    resetBomSkill();
    setOpen(true);
  };

  const handleEdit = (obj, params) => {
    setEditMode(true);
    setErrorFields({});
    setBomSkill(params.row);

    const skillId = params.row.BP_S_Skill;
    const skill = skills.find(x => x.ItemID === skillId);

    if (skill && skill.SkillCategory) {
      setCategory(skill.SkillCategory);
      const categorySkills = skills.filter(x => x.SkillCategory === skill.SkillCategory);
      setFilteredSkills(categorySkills);
    }

    setOpen(true);
    enqueueSnackbar('Editing');
  };

  const handleDeleteClick = (ob, params) => {
    setSkillToDelete(params.row);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!skillToDelete) return;

    axios.delete(`/api/skills/${skillToDelete["BPSkillsID"]}`)
      .then(() => {
        enqueueSnackbar("Skill Deleted Successfully");
        getData();
        handleDeleteConfirmClose();
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(err?.response?.data?.error || "Skill Delete Failed", { variant: 'error' });
        handleDeleteConfirmClose();
      });
  };

  function handleFieldChange(input) {
    const target = input.target;
    const value = target.value;
    const name = target.id;
    setBomSkill(prev => ({ ...prev, [name]: value }));
    if (errorFields[name]) {
      setErrorFields({ ...errorFields, [name]: false });
    }
  }

  function handleSkillChange(input) {
    const target = input.target;
    const value = parseInt(target.value);
    const name = target.id;
    setBomSkill(prev => ({ ...prev, [name]: value }));
    if (errorFields[name]) {
      setErrorFields({ ...errorFields, [name]: false });
    }
  }

  function handleCategoryChange(input) {
    const value = input.target.value;
    setCategory(value);
    const newFilterSkills = skills.filter(x => x.SkillCategory === value);
    setFilteredSkills(newFilterSkills);
    setBomSkill(prev => ({ ...prev, BP_S_Skill: '' }));
    if (errorFields.BP_S_Category) {
      setErrorFields({ ...errorFields, BP_S_Category: false });
    }
  }

  const handleSubmit = async e => {
    e.preventDefault();

    const errors = {};
    if (!category) {
      errors.BP_S_Category = true;
      enqueueSnackbar("Please select a category", { variant: 'error' });
    }
    if (!bomSkill.BP_S_Skill) {
      errors.BP_S_Skill = true;
      enqueueSnackbar("Please select a skill", { variant: 'error' });
    }
    if (!bomSkill.BP_S_Years) {
      errors.BP_S_Years = true;
      enqueueSnackbar("Please select years of experience", { variant: 'error' });
    }
    if (!bomSkill.BP_S_Status) {
      errors.BP_S_Status = true;
      enqueueSnackbar("Please select a status", { variant: 'error' });
    }

    if (Object.keys(errors).length > 0) {
      setErrorFields(errors);
      return;
    }
    setErrorFields({});
    setSaving(true);

    const request = editMode
      ? axios.put('/api/skills', bomSkill)
      : axios.post('/api/skills', bomSkill);

    try {
      await request;
      enqueueSnackbar(editMode ? "Skill Updated" : "Skill Saved");
      setTimeout(() => getData(), 300);
      handleClose();
      resetBomSkill();
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar(
        (editMode ? "Skill Update Failed: " : "Skill Save Failed: ") + (ex.response?.data?.message || ex.message),
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const getData = useCallback(() => {
    if (!user?.UserGUID) return;

    setLoading(true);
    Promise.all([
      axios.get('/api/basic/BP_SkillsQuailifications'),
      axios.get(`/api/skills/${user.UserGUID}`),
    ])
      .then((response) => {
        setSkills(response[0].data);
        setUserSkills(response[1].data);
      })
      .catch((error) => {
        console.error('Error loading skills:', error);
        enqueueSnackbar('Failed to load skills', { variant: 'error' });
      })
      .finally(() => setLoading(false));
  }, [user?.UserGUID, enqueueSnackbar]);

  useEffect(() => {
    getData();
  }, [getData]);

  const columns = [
    {
      field: 'BP_S_Skill', headerName: 'Skill', flex: 2, minWidth: 200,
      valueGetter: (cellValues) => {
        const skill = skills.find(x => x.ItemID === cellValues.value);
        return skill ? skill.Skill : '';
      },
    },
    { field: 'BP_S_License', headerName: 'License', flex: 1, minWidth: 100 },
    { field: 'BP_S_Years', headerName: 'Experience', flex: 1, minWidth: 100 },
    { field: 'BP_S_Status', headerName: 'Status', flex: 1, minWidth: 100 },
    {
      field: "Actions", flex: 1.5, minWidth: 200,
      renderCell: (cellValues) => (
        <div className="d-flex gap-2">
          <Button variant="contained" color="primary" onClick={(event) => handleEdit(event, cellValues)}>
            Edit
          </Button>
          <Button variant="contained" color="error" onClick={(event) => handleDeleteClick(event, cellValues)}>
            Delete
          </Button>
        </div>
      )
    },
  ];

  return (
    <div id="tab2">
      <div className="row px-5 pt-5">
        <div className="col-md-12">
          <DataGrid
            flexGrow
            autoHeight={true}
            autoPageSize={true}
            pageSize={10}
            columns={columns}
            rows={userSkills}
            getRowId={(row) => row.BPSkillsID}
            disableMultipleSelection={true}
            disableSelectionOnClick={true}
            loading={loading}
          />
        </div>
        <div className="col-md-12"> <a data-toggle="collapse" onClick={() => handleAdd()} role="button" aria-expanded="false" aria-controls="add-skills" className="button-default collapsed">Add Skill</a>
          <div id="add">
            <Modal open={open} onClose={handleClose} aria-labelledby='title' aria-describedby=''>
              <Box sx={modalStyle}>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                >
                  <CloseIcon />
                </IconButton>
                <Typography id="title" variant='h5'>Skills</Typography>
                <div className="row pt-3">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="required-caption" htmlFor="BP_S_Category">Category<span className="required-field">*</span></label>
                      <select name="BP_S_Category" id="BP_S_Category" value={category} onChange={handleCategoryChange} className={`DropDownField form-control ${errorFields.BP_S_Category ? 'error-field' : ''}`}>
                        <option value="">Please select</option>
                        {categories.map((cat) => <option key={cat.value} value={cat.value}>{cat.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="required-caption" htmlFor="BP_S_Skill">Skill<span className="required-field">*</span></label>
                      <select name="BP_S_Skill" id="BP_S_Skill" value={bomSkill.BP_S_Skill} onChange={handleSkillChange} className={`DropDownField form-control ${errorFields.BP_S_Skill ? 'error-field' : ''}`}>
                        <option value="">Please select</option>
                        {(filteredSkills.length > 0 ? filteredSkills : skills).map((obj) => (
                          <option key={obj.ItemID} value={obj.ItemID}>{obj.Skill}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="required-caption" htmlFor="BP_S_Years">Years of Experience<span className="required-field">*</span></label>
                      <select name="BP_S_Years" id="BP_S_Years" value={bomSkill.BP_S_Years} onChange={handleFieldChange} className={`DropDownField form-control ${errorFields.BP_S_Years ? 'error-field' : ''}`}>
                        <option value="">Please select</option>
                        {numbers.map((num) => <option key={num} value={num}>{num}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="required-caption">License Number</label>
                      <input type="text" name="BP_S_License" id="BP_S_License" maxLength="200" onChange={handleFieldChange} value={bomSkill.BP_S_License} className="form-control" placeholder="Enter License Number if Applicable" />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="required-caption" htmlFor="BP_S_Status">Status<span className="required-field">*</span></label>
                      <select name="BP_S_Status" id="BP_S_Status" value={bomSkill.BP_S_Status} onChange={handleFieldChange} className={`DropDownField form-control ${errorFields.BP_S_Status ? 'error-field' : ''}`}>
                        <option value="">Please select</option>
                        <option key="a" value="Active">Active</option>
                        <option key="r" value="Retired">Retired</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6 text-white py-1 d-flex gap-2 mt-3">
                    <a data-toggle="collapse" role="button" aria-expanded="false" aria-controls="save-skills" onClick={handleSubmit} className="button-default button-green collapsed" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: '38px', padding: '8px 20px', opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                      {saving && <CircularProgress size={14} style={{ color: 'white' }} />}
                      Save
                    </a>
                    <a data-toggle="collapse" onClick={handleClose} role="button" aria-expanded="false" aria-controls="cancel-skills" className="button-default button-gray collapsed">Cancel</a>
                  </div>
                  <div className="col-md-6 text-white py-1 d-flex gap-2 justify-content-end mt-3">
                    {editMode && <a data-toggle="collapse" role="button" aria-expanded="false" aria-controls="delete-skills" className="button-default button-red collapsed">Delete</a>}
                  </div>
                </div>
              </Box>
            </Modal>
          </div>
        </div>

        <Modal open={deleteConfirmOpen} onClose={handleDeleteConfirmClose} aria-labelledby='delete-title' aria-describedby='delete-description'>
          <Box sx={modalStyle}>
            <IconButton
              aria-label="close"
              onClick={handleDeleteConfirmClose}
              sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
            <Typography id="delete-title" variant='h5' sx={{ mb: 2 }}>Confirm Delete</Typography>
            <Typography id="delete-description" variant='body1' sx={{ mb: 4 }}>
              Are you sure you want to delete this entry? This action cannot be undone.
            </Typography>
            <div className="row">
              <div className="col-md-12 text-white py-1 d-flex gap-2">
                <a role="button" onClick={handleDeleteConfirm} className="button-default button-red collapsed">Delete</a>
                <a role="button" onClick={handleDeleteConfirmClose} className="button-default button-gray collapsed">Cancel</a>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </div>
  );
};
export default Skills;